// ============================================================
// PLUMME — AI Advisor: analytical report sections
// ============================================================
// The sections that explain rather than list. Everything here is still pure
// derivation from models the app already owns — but where the financial
// sections answer "what is the level", these answer "what moved it", "how fast
// does money come back", and "what does the trend look like once the calendar
// year stops cutting it in half".
//
// See aiAdvisorReportBlocks.jsx for the block contract these return.
const PlummeAiAdvisorReportAnalysis = (() => {
  const B = () => window.PlummeAiAdvisorReportBlocks;

  // Lines below the materiality threshold are grouped rather than listed. A
  // fixed "top 5" makes a 400-line chart of accounts look like a five-line one;
  // a threshold scaled to revenue keeps the cut honest at any size, with a
  // floor so a tiny business does not end up with a threshold of a few kroner.
  const MATERIALITY_FLOOR = 5000;
  const MATERIALITY_SHARE = 0.005;
  const materialityThreshold = (income) => {
    const { num } = B();
    return Math.max(MATERIALITY_FLOOR, Math.abs(num(income)) * MATERIALITY_SHARE);
  };

  const sectionLines = (report, key) => {
    const { safeArray } = B();
    const section = safeArray(report && report.sections).find((entry) => entry.key === key);
    return safeArray(section && section.lines);
  };

  // ---------- Result bridge ----------
  // Decomposes the change in result into the drivers that caused it, so the
  // reader sees why the number moved instead of only that it did.
  //
  // The identity the section rests on: result = income - costs + finance, so
  // the effects below must sum exactly to the change in result. Revenue enters
  // with its own sign; a cost that grew is a negative effect. Nothing is
  // rounded until the very end, and the closing row is the real result rather
  // than the sum of the rows, so a rounding difference can never make the
  // bridge look like it reconciles when it does not.
  const buildBridgeRows = (ctx) => {
    const { num, round } = B();
    const report = ctx.report || {};
    const previous = ctx.previousReport || {};
    const threshold = materialityThreshold(report.inntekter);

    const effects = [];
    const incomeEffect = num(report.inntekter) - num(previous.inntekter);
    if (incomeEffect) effects.push({ kind: 'income', key: 'adv.rpt.bridge.revenue', effect: incomeEffect });

    // Keyed by account code, but the name is carried along: a line that exists
    // only last year has no name in this year's report, and a bridge row
    // reading "6300" instead of "Leie lokale" is unreadable.
    const previousCosts = new Map(sectionLines(previous, 'kostnader')
      .map((line) => [String(line.nr), { amount: num(line.amount), name: line.name || String(line.nr) }]));
    const seen = new Set();
    sectionLines(report, 'kostnader').forEach((line) => {
      const code = String(line.nr);
      seen.add(code);
      // A cost that rose subtracts from the result, hence the sign flip.
      const effect = -(num(line.amount) - num((previousCosts.get(code) || {}).amount));
      if (effect) effects.push({ kind: 'cost', name: line.name || code, effect });
    });
    // A cost line that existed last year and is gone this year is a real
    // improvement, and dropping it would break the reconciliation.
    previousCosts.forEach((entry, code) => {
      if (seen.has(code)) return;
      if (entry.amount) effects.push({ kind: 'cost', name: entry.name || code, effect: entry.amount });
    });

    const financeEffect = num(report.finans) - num(previous.finans);
    if (financeEffect) effects.push({ kind: 'finance', key: 'adv.rpt.bridge.finance', effect: financeEffect });

    // Put revenue directly after the opening result so the bridge reads as
    // income, then cost/finance drivers. Rank the remaining lines by impact.
    const sorted = effects.sort((a, b) => {
      if (a.kind === 'income' && b.kind !== 'income') return -1;
      if (b.kind === 'income' && a.kind !== 'income') return 1;
      return Math.abs(b.effect) - Math.abs(a.effect);
    });
    const material = sorted.filter((entry) => Math.abs(entry.effect) >= threshold);
    const rest = sorted.filter((entry) => Math.abs(entry.effect) < threshold);
    const restTotal = rest.reduce((total, entry) => total + entry.effect, 0);

    return {
      threshold: round(threshold),
      opening: round(previous.resultat),
      closing: round(report.resultat),
      material,
      restCount: rest.length,
      restTotal: round(restTotal),
    };
  };

  const bridgeSection = (ctx) => {
    const { section, note, empty, money, chart } = B();
    if (!ctx.previousYearAvailable) {
      return section('bro', 'adv.rpt.sec.bridge', [empty('adv.rpt.empty.noBridgeBasis', { year: ctx.year - 1 })]);
    }
    const bridge = buildBridgeRows(ctx);
    if (!bridge.material.length && !bridge.restCount) {
      return section('bro', 'adv.rpt.sec.bridge', [empty('adv.rpt.empty.noBridgeChange', { year: ctx.year - 1 })]);
    }
    const effectCell = (value) => ({ ...money(value), tone: value < 0 ? 'danger' : 'success' });
    const rows = [
      [{ key: 'adv.rpt.bridge.opening', vars: { year: ctx.year - 1 }, tone: 'total' }, { ...money(bridge.opening), tone: 'total' }],
      ...bridge.material.map((entry) => [
        entry.kind === 'income'
          ? { key: entry.key, tone: entry.effect > 0 ? 'success' : 'danger' }
          : entry.key ? { key: entry.key } : { text: entry.name },
        effectCell(entry.effect),
      ]),
    ];
    if (bridge.restCount) {
      rows.push([{ key: 'adv.rpt.bridge.other', vars: { count: bridge.restCount } }, effectCell(bridge.restTotal)]);
    }
    rows.push([{ key: 'adv.rpt.bridge.closing', vars: { year: ctx.year }, tone: 'total' }, { ...money(bridge.closing), tone: 'total' }]);

    // The waterfall and the table are the same rows twice. The chart makes the
    // shape of the change obvious at a glance; the table is what survives into
    // the PDF, a printout and a screen reader.
    const steps = [
      ...bridge.material.map((entry) => entry.effect),
      ...(bridge.restCount ? [bridge.restTotal] : []),
    ];
    const stepLabels = [
      ...bridge.material.map((entry) => (entry.key ? { key: entry.key } : { text: entry.name })),
      ...(bridge.restCount ? [{ key: 'adv.rpt.bridge.other', vars: { count: bridge.restCount } }] : []),
    ];
    return section('bro', 'adv.rpt.sec.bridge', [
      chart('waterfall', {
        opening: bridge.opening,
        closing: bridge.closing,
        openingLabel: { key: 'adv.rpt.bridge.opening', vars: { year: ctx.year - 1 } },
        closingLabel: { key: 'adv.rpt.bridge.closing', vars: { year: ctx.year } },
        categories: stepLabels,
        series: [{ id: 'effect', labelKey: 'adv.rpt.col.effect', values: steps }],
      }),
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.driver' }, { key: 'adv.rpt.col.effect' }],
        rows,
      },
      note('info', 'adv.rpt.note.bridgeBasis', { amount: bridge.threshold, year: ctx.year - 1 }),
    ]);
  };

  // ---------- Working capital ----------
  // How long money is tied up before it comes back. Days are measured against
  // the booked period, not a full year: annualising nine months of revenue
  // would understate the days and make a stretched business look healthy.
  const workingCapitalSection = (ctx) => {
    const { num, round, round1, section, note, empty, chart } = B();
    const overview = (ctx.home && ctx.home.overview) || {};
    const receivables = num(overview.salesCurrent) + num(overview.salesOverdue);
    const payables = num(overview.purchasesCurrent) + num(overview.purchasesOverdue);
    const income = num(ctx.report && ctx.report.inntekter);
    const costs = num(ctx.report && ctx.report.kostnader);
    const periodDays = Math.max(1, (num(ctx.monthsElapsed) / 12) * 365);
    if (!receivables && !payables) {
      return section('arbeidskapital', 'adv.rpt.sec.workingCapital', [empty('adv.rpt.empty.noWorkingCapital')]);
    }
    const receivableDays = income > 0 ? (receivables / income) * periodDays : null;
    const payableDays = costs > 0 ? (payables / costs) * periodDays : null;
    const gap = receivableDays == null || payableDays == null ? null : receivableDays - payableDays;
    const measured = ctx.sales && ctx.sales.kpis ? ctx.sales.kpis.avgPaymentDays : null;
    const netTied = receivables - payables;
    return section('arbeidskapital', 'adv.rpt.sec.workingCapital', [
      {
        type: 'metrics',
        items: [
          { id: 'receivableDays', label: { key: 'adv.rpt.metric.receivableDays' }, value: receivableDays == null ? null : round(receivableDays), format: 'days' },
          { id: 'payableDays', label: { key: 'adv.rpt.metric.payableDays' }, value: payableDays == null ? null : round(payableDays), format: 'days' },
          {
            id: 'gap',
            label: { key: 'adv.rpt.metric.capitalGap' },
            value: gap == null ? null : round(gap),
            format: 'days',
            tone: gap != null && gap > 0 ? 'danger' : 'success',
          },
          { id: 'measured', label: { key: 'adv.rpt.metric.measuredPaymentDays' }, value: measured == null ? null : round1(measured), format: 'days' },
        ],
      },
      chart('waterfall', {
        compact: true,
        titleKey: 'adv.rpt.chart.workingCapitalGap',
        opening: round(receivables),
        openingLabel: { key: 'adv.rpt.metric.receivables' },
        categories: [{ key: 'adv.rpt.metric.payables' }],
        series: [{ id: 'payables', labelKey: 'adv.rpt.metric.payables', values: [-round(payables)] }],
        closing: round(netTied),
        closingLabel: { key: 'adv.rpt.metric.netCapitalTied' },
      }),
      gap == null
        ? note('info', 'adv.rpt.note.capitalGapUnknown')
        : gap > 0
          ? note('warning', 'adv.rpt.note.capitalGapPositive', { days: round(gap) })
          : note('success', 'adv.rpt.note.capitalGapNegative'),
      note('info', 'adv.rpt.note.workingCapitalBasis', { months: ctx.monthsElapsed }),
    ]);
  };

  // ---------- Rolling twelve months ----------
  // The calendar year cuts the trend in half every January. Twelve booked
  // months ending this month, against the twelve before them, removes both the
  // year boundary and the seasonal shape from the comparison.
  const rollingWindow = (current, previous, monthsElapsed) => {
    const { num, safeArray } = B();
    const months = Math.max(0, Math.min(12, num(monthsElapsed)));
    const take = (chart, from, to) => safeArray(chart).slice(from, to);
    return [
      ...take(previous, months, 12),
      ...take(current, 0, months),
    ].reduce((totals, month) => ({
      inc: totals.inc + num(month.inc),
      cost: totals.cost + num(month.cost),
    }), { inc: 0, cost: 0 });
  };

  const rollingSection = (ctx) => {
    const { num, round, round1, percentChange, deltaTone, section, note, empty } = B();
    const months = num(ctx.rollingMonths ?? ctx.monthsElapsed);
    if (!months || !ctx.previousChart || !ctx.previousYearAvailable) {
      return section('rullerende', 'adv.rpt.sec.rolling', [empty('adv.rpt.empty.noRollingBasis')]);
    }
    const now = rollingWindow(ctx.rollingChart || ctx.chart, ctx.previousChart, months);
    const before = rollingWindow(ctx.previousChart, ctx.previous2Chart, months);
    if (!now.inc && !now.cost) {
      return section('rullerende', 'adv.rpt.sec.rolling', [empty('adv.rpt.empty.noRollingBasis')]);
    }
    const hasComparison = Boolean(before.inc || before.cost);
    const result = now.inc - now.cost;
    const previousResult = before.inc - before.cost;
    const withDelta = (value, previous, betterWhen) => {
      if (!hasComparison) return {};
      const delta = percentChange(value, previous);
      return delta == null ? {} : { delta, deltaTone: deltaTone(delta, betterWhen), compare: { key: 'adv.rpt.cell.vsPrevious12' } };
    };
    return section('rullerende', 'adv.rpt.sec.rolling', [
      {
        type: 'kpis',
        items: [
          { id: 'rollingIncome', label: { key: 'adv.rpt.kpi.income' }, value: round(now.inc), format: 'currency', ...withDelta(now.inc, before.inc, 'high') },
          { id: 'rollingCosts', label: { key: 'adv.rpt.kpi.costs' }, value: round(now.cost), format: 'currency', ...withDelta(now.cost, before.cost, 'low') },
          { id: 'rollingResult', label: { key: 'adv.rpt.kpi.result' }, value: round(result), format: 'currency', ...withDelta(result, previousResult, 'high') },
          {
            id: 'rollingMargin',
            label: { key: 'adv.rpt.kpi.margin' },
            value: now.inc > 0 ? round1((result / now.inc) * 100) : null,
            format: 'percent',
          },
        ],
      },
      hasComparison ? null : note('info', 'adv.rpt.note.noRollingComparison'),
      note('info', 'adv.rpt.note.rollingBasis', { months: 12 }),
    ]);
  };

  // ---------- Change since the last report ----------
  // A board reads reports in sequence. The question at the top of the second
  // one is always "what is different since last time", and only a stored
  // snapshot can answer it — today's figures cannot say what yesterday's
  // report claimed.
  const docKpiValues = (doc) => {
    const { safeArray } = B();
    const section = safeArray(doc && doc.sections).find((entry) => entry.id === 'hovedtall');
    const block = safeArray(section && section.blocks).find((entry) => entry.type === 'kpis');
    const values = {};
    safeArray(block && block.items).forEach((item) => {
      if (item && item.value != null) values[item.id] = { value: item.value, format: item.format };
    });
    return values;
  };

  const CHANGE_LABELS = {
    income: 'adv.rpt.kpi.income',
    costs: 'adv.rpt.kpi.costs',
    result: 'adv.rpt.kpi.result',
    margin: 'adv.rpt.kpi.margin',
  };

  const changeSinceSection = (ctx) => {
    const { num, round, round1, section, note, empty } = B();
    const previousDoc = ctx.previousDoc;
    if (!previousDoc) {
      return section('endring', 'adv.rpt.sec.changeSince', [empty('adv.rpt.empty.noPreviousReport')]);
    }
    const before = docKpiValues(previousDoc);
    const income = num(ctx.report && ctx.report.inntekter);
    const result = num(ctx.report && ctx.report.resultat);
    const now = {
      income: { value: round(income), format: 'currency' },
      costs: { value: round(ctx.report && ctx.report.kostnader), format: 'currency' },
      result: { value: round(result), format: 'currency' },
      margin: { value: income > 0 ? round1((result / income) * 100) : null, format: 'percent' },
    };
    const rows = Object.keys(CHANGE_LABELS)
      .filter((id) => before[id] && now[id] && now[id].value != null)
      .map((id) => {
        const delta = num(now[id].value) - num(before[id].value);
        return [
          { key: CHANGE_LABELS[id] },
          { value: before[id].value, format: before[id].format },
          { value: now[id].value, format: now[id].format },
          { value: round1(delta), format: now[id].format, tone: delta === 0 ? null : (id === 'costs') === (delta < 0) ? 'success' : 'danger' },
        ];
      });
    if (!rows.length) {
      return section('endring', 'adv.rpt.sec.changeSince', [empty('adv.rpt.empty.noComparableReport')]);
    }
    return section('endring', 'adv.rpt.sec.changeSince', [
      {
        type: 'table',
        cols: [{ key: 'adv.rpt.col.line' }, { key: 'adv.rpt.col.lastReport' }, { key: 'adv.rpt.col.now' }, { key: 'adv.rpt.col.change' }],
        rows,
      },
      note('info', 'adv.rpt.note.changeSinceBasis', { date: String(previousDoc.generatedAt || '').slice(0, 10) }),
    ]);
  };

  return {
    bridgeSection,
    workingCapitalSection,
    rollingSection,
    changeSinceSection,
    // Exported for the tests that pin the reconciliation and the threshold.
    helpers: { buildBridgeRows, materialityThreshold, rollingWindow, docKpiValues, MATERIALITY_FLOOR, MATERIALITY_SHARE },
  };
})();

Object.assign(window, { PlummeAiAdvisorReportAnalysis });
